import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionIcon from '@mui/icons-material/Construction';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Swal from 'sweetalert2';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import './Reporting.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography color="error" className="reporting-error">
          Something went wrong: {this.state.error?.message || 'Unknown error'}
        </Typography>
      );
    }
    return this.props.children;
  }
}

const Reporting = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [showDevBanner, setShowDevBanner] = useState(true);

  const mockReports = [
    {
      id: 'report1',
      name: 'Q1 Compliance Report',
      type: 'Compliance',
      status: 'Completed',
      user: 'Alice',
      details: 'Summary of sanctions checks.',
      date: '2025-10-01',
      department: 'Compliance',
    },
    {
      id: 'report2',
      name: 'Price Analysis Jan',
      type: 'Financial',
      status: 'Pending',
      user: 'Bob',
      details: 'Price anomaly trends.',
      date: '2025-10-02',
      department: 'Financial',
    },
    {
      id: 'report3',
      name: 'Export Audit',
      type: 'Audit',
      status: 'Completed',
      user: 'Charlie',
      details: 'Export transaction review.',
      date: '2025-10-03',
      department: 'Audit',
    },
    {
      id: 'report4',
      name: 'Q2 Sanctions Report',
      type: 'Compliance',
      status: 'Failed',
      user: 'Alice',
      details: 'Sanctions data incomplete.',
      date: '2025-10-04',
      department: 'Compliance',
    },
    {
      id: 'report5',
      name: 'Financial Summary',
      type: 'Financial',
      status: 'Completed',
      user: 'Bob',
      details: 'Revenue and costs.',
      date: '2025-10-05',
      department: 'Financial',
    },
  ];

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        setReports(mockReports);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports: ' + (err.message || 'Unknown error'));
        setLoading(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load reporting data.',
        });
      }
    };

    fetchReports();
  }, []);

  const filteredReports = reports.filter(
    (r) =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pie Chart: Reports by Status
  const statusCounts = filteredReports.reduce(
    (acc, report) => {
      acc[report.status] = (acc[report.status] || 0) + 1;
      return acc;
    },
    { Completed: 0, Pending: 0, Failed: 0 }
  );

  const pieData = {
    labels: ['Completed', 'Pending', 'Failed'],
    datasets: [
      {
        data: [statusCounts.Completed, statusCounts.Pending, statusCounts.Failed],
        backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
        hoverBackgroundColor: ['#059669', '#d97706', '#dc2626'],
        borderColor: 'var(--border-color)',
        borderWidth: 1,
      },
    ],
  };

  // Line Chart: Reports Over Time
  const dates = [...new Set(filteredReports.map((r) => r.date))].sort();
  const reportsByDate = dates.map((date) => ({
    date,
    count: filteredReports.filter((r) => r.date === date).length,
  }));

  const lineData = {
    labels: dates,
    datasets: [
      {
        label: 'Reports',
        data: reportsByDate.map((d) => d.count),
        fill: false,
        borderColor: 'var(--primary-color)',
        backgroundColor: 'var(--primary-color)',
        tension: 0.3,
        pointBackgroundColor: 'var(--text-color)',
        pointBorderColor: 'var(--primary-color)',
        pointBorderWidth: 2,
      },
    ],
  };

  // Bar Chart: Reports by Department
  const types = [...new Set(filteredReports.map((r) => r.department))];
  const reportsByType = types.map((type) => ({
    type,
    count: filteredReports.filter((r) => r.department === type).length,
  }));

  const barData = {
    labels: types,
    datasets: [
      {
        label: 'Reports',
        data: reportsByType.map((t) => t.count),
        backgroundColor: 'rgba(102, 126, 234, 0.6)',
        hoverBackgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'var(--border-color)',
        borderWidth: 1,
      },
    ],
  };

  // Doughnut Chart: Reports by User
  const users = [...new Set(filteredReports.map((r) => r.user))];
  const reportsByUser = users.map((user) => ({
    user,
    count: filteredReports.filter((r) => r.user === user).length,
  }));

  const doughnutData = {
    labels: users,
    datasets: [
      {
        data: reportsByUser.map((u) => u.count),
        backgroundColor: ['#ff6b6b', '#c084fc', '#3b82f6'],
        hoverBackgroundColor: ['#ef4444', '#a855f7', '#2563eb'],
        borderColor: 'var(--border-color)',
        borderWidth: 1,
      },
    ],
  };

  const getChartTextColor = () => {
    const theme = document.documentElement.classList.contains('light-theme') ? 'light' : 'dark';
    return theme === 'light' ? '#1f2937' : '#ffffff';
  };

  const getBorderColor = () => {
    return getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim();
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          color: getChartTextColor(),
          font: { family: 'Inter', size: 12, weight: 500 },
          padding: 15,
        },
      },
      tooltip: { 
        enabled: true,
        backgroundColor: 'rgba(26, 32, 63, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: getBorderColor(),
        borderWidth: 1,
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart',
    },
    scales: {
      x: { 
        display: true,
        grid: { color: getBorderColor() },
        ticks: { color: getChartTextColor(), font: { family: 'Inter', size: 11 } },
      },
      y: { 
        beginAtZero: true,
        grid: { color: getBorderColor() },
        ticks: { color: getChartTextColor(), font: { family: 'Inter', size: 11 } },
      },
    },
  };

  const columns = [
    { field: 'name', headerName: 'Report Name', flex: 2, minWidth: 200 },
    { field: 'department', headerName: 'Department', flex: 1.5, minWidth: 150 },
    { field: 'type', headerName: 'Type', flex: 1.5, minWidth: 150 },
    { field: 'status', headerName: 'Status', flex: 1, minWidth: 120 },
    { field: 'user', headerName: 'Generated By', flex: 1, minWidth: 120 },
    { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Button 
          size="small" 
          variant="contained"
          onClick={() => handleOpenDetails(params.row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const handleOpenDetails = (report) => {
    setSelectedReport(report);
    setOpenDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsModal(false);
    setSelectedReport(null);
  };

  if (loading) {
    return (
      <Box className="reporting-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: 'var(--primary-color)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="reporting-container" sx={{ p: 3 }}>
        <Typography color="error" className="reporting-error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box className="reporting-container">
        <div className="reporting-particles">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="reporting-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        {/* Under Development Banner */}
        <Collapse in={showDevBanner}>
          <Alert
            severity="warning"
            icon={<ConstructionIcon fontSize="inherit" />}
            className="dev-banner"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShowDevBanner(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{
              mb: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderLeft: '4px solid #ff9800',
              '& .MuiAlert-icon': {
                color: '#ff9800',
              },
              '& .MuiAlert-message': {
                color: 'var(--text-color)',
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Under Development</AlertTitle>
            This page is currently under development. Some features may not work as expected.
          </Alert>
        </Collapse>

        <TextField
          label="Search Reports"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          variant="outlined"
          className="reporting-search"
          sx={{ maxWidth: '400px', mb: 3 }}
        />

        <Box className="reporting-section">
          <Typography variant="h6" className="reporting-section-title">
            Report List
          </Typography>
          <Box className="reporting-table-wrapper">
            <DataGrid
              rows={filteredReports}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              components={{ Toolbar: GridToolbar }}
              className="reporting-data-grid"
              rowHeight={60}
              sx={{ 
                height: '500px',
                '& .MuiDataGrid-cell': {
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  background: 'var(--gradient-primary)',
                  color: '#ffffff',
                },
                '& .MuiDataGrid-row': {
                  minHeight: '60px !important',
                  maxHeight: '60px !important',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                },
              }}
              getRowId={(row) => row.id}
            />
          </Box>
        </Box>

        <Box className="reporting-section">
          <Typography variant="h6" className="reporting-section-title">
            Report Insights
          </Typography>
          <Box className="reporting-charts-grid">
            <Box className="reporting-chart-card card">
              <Typography variant="subtitle1" className="reporting-chart-title">
                Reports by Status
              </Typography>
              <div className="reporting-chart-wrapper">
                <Pie data={pieData} options={chartOptions} />
              </div>
            </Box>
            <Box className="reporting-chart-card card">
              <Typography variant="subtitle1" className="reporting-chart-title">
                Reports Over Time
              </Typography>
              <div className="reporting-chart-wrapper">
                <Line data={lineData} options={chartOptions} />
              </div>
            </Box>
            <Box className="reporting-chart-card card">
              <Typography variant="subtitle1" className="reporting-chart-title">
                Reports by Department
              </Typography>
              <div className="reporting-chart-wrapper">
                <Bar data={barData} options={chartOptions} />
              </div>
            </Box>
            <Box className="reporting-chart-card card">
              <Typography variant="subtitle1" className="reporting-chart-title">
                Reports by User
              </Typography>
              <div className="reporting-chart-wrapper">
                <Doughnut data={doughnutData} options={chartOptions} />
              </div>
            </Box>
          </Box>
        </Box>

        <Dialog 
          open={openDetailsModal} 
          onClose={handleCloseDetails}
          PaperProps={{
            className: 'modal'
          }}
        >
          <DialogTitle className="reporting-dialog-title">
            {selectedReport?.name}
          </DialogTitle>
          <DialogContent className="reporting-dialog-content">
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Department:</strong> {selectedReport?.department}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Type:</strong> {selectedReport?.type}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {selectedReport?.status}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Generated By:</strong> {selectedReport?.user}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Date:</strong> {selectedReport?.date}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              {selectedReport?.details}
            </Typography>
          </DialogContent>
          <DialogActions className="reporting-dialog-actions">
            <Button variant="contained" onClick={handleCloseDetails}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ErrorBoundary>
  );
};

export default Reporting;